import {
  CreateBase,
  Form,
  useDataProvider,
  useGetIdentity,
  useNotify,
  useRedirect,
  useRefresh,
  useTranslate,
} from "ra-core";
import { SaveButton } from "@/components/admin/form";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";

import { fullScreenOnMobile } from "../layout/mobileDialog";
import type { Sponsoring } from "../types";
import { SponsoringInputs } from "./SponsoringInputs";

/**
 * Create dialog. On a sponsorings overview it is opened by the route
 * `${basePath}/create` and closes by redirecting to that list; elsewhere
 * (the dashboard) the caller controls it with `open`/`onClose` and can
 * prefill fields with `defaultValues`.
 */
export const SponsoringCreate = ({
  open,
  onClose,
  defaultValues,
  basePath = "/sponsorings",
}: {
  open: boolean;
  onClose?: () => void;
  defaultValues?: Partial<Sponsoring>;
  /** The overview this dialog belongs to (/sponsorings, /balsponsoren, /leads) */
  basePath?: string;
}) => {
  const redirect = useRedirect();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  const translate = useTranslate();
  const { identity } = useGetIdentity();

  const handleClose = () => (onClose ? onClose() : redirect(basePath));

  const onSuccess = async (sponsoring: Sponsoring) => {
    // A new sponsoring goes to the top of its board column (index 0):
    // shift the other cards of that column down
    const { data: column } = await dataProvider.getList<Sponsoring>(
      "sponsorings",
      {
        filter: { stage: sponsoring.stage },
        sort: { field: "index", order: "ASC" },
        pagination: { page: 1, perPage: 100 },
      },
    );
    await Promise.all(
      column
        .filter((other) => other.id !== sponsoring.id)
        .map((other) =>
          dataProvider.update("sponsorings", {
            id: other.id,
            data: { index: other.index + 1 },
            previousData: other,
          }),
        ),
    );
    notify("ra.notification.created", {
      type: "info",
      messageArgs: { smart_count: 1 },
    });
    refresh();
    handleClose();
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
      <DialogContent
        className={`lg:max-w-2xl overflow-y-auto max-h-9/10 top-1/20 translate-y-0 ${fullScreenOnMobile}`}
        aria-describedby={undefined}
      >
        <DialogTitle>
          {translate(
            defaultValues?.kind === "ball"
              ? "resources.sponsorings.action.create_ball"
              : "resources.sponsorings.action.create",
          )}
        </DialogTitle>
        {open ? (
          <CreateBase
            resource="sponsorings"
            redirect={false}
            mutationOptions={{ onSuccess }}
          >
            <Form
              defaultValues={{
                sales_id: identity?.id,
                index: 0,
                kind: "sponsoring",
                ...defaultValues,
              }}
            >
              <SponsoringInputs />
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="ghost" onClick={handleClose}>
                  {translate("ra.action.cancel")}
                </Button>
                <SaveButton />
              </div>
            </Form>
          </CreateBase>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};
